import React, { useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import LoadingIndicator from '@diditusa/shared/web_components/LoadingIndicator/loadingIndicator';
import BasicTable from '@diditusa/shared/web_components/BasicTable/BasicTable';
import { useLazyGetCompanyJobsQuery } from '@diditusa/shared/api/companyApi';
import { Job, JobStatus } from '@diditusa/shared/types/jobTypes';
import { getErrorMessage } from '@diditusa/shared/api/apiUtils';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { BasicTableHeader } from '@diditusa/shared/types/commonTypes';
import { DateTime } from 'luxon';
const CompanyJobs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.company?.id;
  const [trigger, { data: jobs, isLoading, isError, error: getJobsError }] =
    useLazyGetCompanyJobsQuery();

  useEffect(() => {
    if (companyId) {
      trigger(companyId);
    }
  }, [companyId, trigger]);

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='200px'>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box>
        <Typography color='error'>Error fetching jobs.</Typography>
      </Box>
    );
  }

  const renderJobs = () => {
    if (isLoading) {
      return <LoadingIndicator isVisible={isLoading} />;
    }

    if (isError) {
      return <p>Error loading jobs: {getErrorMessage(getJobsError)}</p>;
    }

    interface JobDetails {
      id: number;
      reservationCode: string;
      user: string;
      location: string;
      agents: string;
      startDate: DateTime;
      startTime: DateTime;
      duration: number;
      package: string;
      status: JobStatus;
    }

    type JobDetailsKey = keyof JobDetails;
    const jobDetails: JobDetails[] =
      jobs?.map((j) => ({
        id: j.id,
        reservationCode: j.reservationCode,
        location: j.location,
        user: j.createdBy ? `${j.createdBy.firstName} ${j.createdBy.lastName}` : j.createdById,
        agents:
          j.jobAssignments
            ?.map((ja) => (ja.user ? `${ja.user.firstName} ${ja.user.lastName}` : ja.userId))
            .join(',') || '',
        startTime: j.startTime,
        startDate: j.startTime,
        status: j.status,
        duration: j.durationHours,
        package: j.securityOption?.title || '-',
      })) || [];

    const headers: BasicTableHeader<JobDetailsKey>[] = [
      { key: 'reservationCode', value: 'Reservation Number' },
      { key: 'user', value: 'Customer' },
      { key: 'agents', value: 'Assigned Agent' },
      { key: 'location', value: 'Location' },
      { key: 'startDate', value: 'Start Date' },
      { key: 'startTime', value: 'Start Time' },
      { key: 'duration', value: 'Duration' },
      { key: 'package', value: 'Package' },
      { key: 'status', value: 'Status' },
    ];

    const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
      const c = jobDetails[idx];
      if (field === 'reservationCode') {
        return (
          <Button onClick={() => navigate(`/company/jobs/${c.id}`)}>{c.reservationCode}</Button>
        );
      }
      if (field === 'startDate') {
        return c.startDate.toFormat('MM/dd/yyyy');
      }

      if (field === 'startTime') {
        return c.startTime.toFormat('hh:mm a ZZZZ');
      }
    };

    return (
      <Box sx={{ mb: 3 }}>
        <BasicTable
          headers={headers}
          data={jobDetails}
          getCustomComponentForField={getCustomComponentForCell}
        />
      </Box>
    );
  };

  return (
    <Box minHeight='100vh' display='flex' flexDirection='row' bgcolor='white'>
      <Box flex={1} maxWidth={'90%'} mx='auto'>
        <Box
          display='flex'
          flexDirection={'row'}
          justifyContent='space-between'
          alignItems='center'
          p={2}
        >
          <Typography variant='h4' fontWeight={700} color='#111418'>
            {user?.company?.name}: All Jobs
          </Typography>
        </Box>
        {renderJobs()}
      </Box>
    </Box>
  );
};

export default CompanyJobs;
